import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/web/components/ui/button";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Textarea } from "@/web/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/web/components/ui/select";

const schema = z.object({
  name: z.string().min(2, "Please tell us your name."),
  email: z.string().email("Please enter a valid email."),
  phone: z.string().min(6, "Please enter a contact phone number."),
  address: z.string().min(4, "Please enter the property address."),
  serviceType: z.enum(["Heat Pump Install", "Heat Pump Service", "Refrigeration Service", "Air Conditioning", "Home Ventilation", "Not Sure"]),
  buildingType: z.enum(["Residential", "Commercial"]),
  message: z.string().min(10, "Please give us a few details about your project."),
  website: z.string().max(0).optional(), // honeypot
});

type FormValues = z.infer<typeof schema>;

export default function QuoteForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      message: "",
      website: "",
    },
  });

  const serviceType = watch("serviceType");
  const buildingType = watch("buildingType");

  const onSubmit = async (values: FormValues) => {
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch("/api/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setStatus("success");
      reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-6">
        <div className="mb-2 flex items-center gap-2 text-green-700">
          <CheckCircle2 className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Thanks — message received.</h3>
        </div>
        <p className="text-sm text-green-800">
          We've received your enquiry and will be in touch within one business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Honeypot */}
      <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} className="hidden" aria-hidden />

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input id="name" {...register("name")} placeholder="Your full name" className="mt-1.5" />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" {...register("email")} placeholder="you@example.com" className="mt-1.5" />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" type="tel" {...register("phone")} placeholder="021 ..." className="mt-1.5" />
          {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone.message}</p>}
        </div>
        <div>
          <Label htmlFor="address">Address</Label>
          <Input id="address" {...register("address")} placeholder="Project location" className="mt-1.5" />
          {errors.address && <p className="mt-1 text-xs text-destructive">{errors.address.message}</p>}
        </div>
        <div>
          <Label>Service Type</Label>
          <Select value={serviceType} onValueChange={(v) => setValue("serviceType", v as FormValues["serviceType"], { shouldValidate: true })}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select a service" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Heat Pump Install">Heat Pump Install</SelectItem>
              <SelectItem value="Heat Pump Service">Heat Pump Service</SelectItem>
              <SelectItem value="Refrigeration Service">Refrigeration Service</SelectItem>
              <SelectItem value="Air Conditioning">Air Conditioning</SelectItem>
              <SelectItem value="Home Ventilation">Home Ventilation</SelectItem>
              <SelectItem value="Not Sure">Not Sure</SelectItem>
            </SelectContent>
          </Select>
          {errors.serviceType && <p className="mt-1 text-xs text-destructive">Please choose a service.</p>}
        </div>
        <div>
          <Label>Building Type</Label>
          <Select value={buildingType} onValueChange={(v) => setValue("buildingType", v as FormValues["buildingType"], { shouldValidate: true })}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Residential or Commercial" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Residential">Residential</SelectItem>
              <SelectItem value="Commercial">Commercial</SelectItem>
            </SelectContent>
          </Select>
          {errors.buildingType && <p className="mt-1 text-xs text-destructive">Please choose a building type.</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="message">Tell us about your project</Label>
        <Textarea id="message" {...register("message")} placeholder="Number of rooms / sqm, existing system, timeframe, anything we should know..." className="mt-1.5" />
        {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message.message}</p>}
      </div>

      {status === "error" && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>Sorry — we couldn't send your enquiry. Please try again, or call us on <a href="tel:0272980080" className="cursor-pointer font-semibold underline underline-offset-2">027 298 0080</a>. {errorMsg && `(${errorMsg})`}</span>
        </div>
      )}

      <Button type="submit" size="lg" disabled={status === "loading"} className="w-full sm:w-auto">
        {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send enquiry"}
      </Button>
    </form>
  );
}
